import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { ButtonComponent } from '../../input/button/button.component';
import { DropdownComponent } from '../../input/dropdown/dropdown.component';
import { RadioGroupComponent } from '../../input/radio-button/radio-group.component';
import { TextComponent } from '../../input/text/text.component';
import { ModalComponent } from '../../layout/modal/modal.component';
import { ModalService } from '../../layout/modal/modal.service';
import { IconComponent } from '../../presentation/icon/icon.component';
import { LabelComponent } from '../../presentation/label/label.component';
import { ToasterService } from '../../presentation/toast/toaster.service';
import {
  EditableViewSettingsGroup,
  ViewGroup,
  ViewSettings,
} from '@aksia/models';
import { ICONS, UTILS } from '@aksia/infrastructure';
import { UiPopupDirective } from '../../../directives/ui/ui-popup.directive';

type TViewEntityOption = {
  label: string;
  value: string;
  group: string;
  view: ViewSettings;
};

@Component({
  selector: 'views-menu',
  imports: [
    LabelComponent,
    DropdownComponent,
    IconComponent,
    ButtonComponent,
    RadioGroupComponent,
    TextComponent,
    ModalComponent,
    UiPopupDirective,
  ],
  templateUrl: './views-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewsMenuComponent {
  //#region Injections

  private readonly toasterService = inject(ToasterService);
  private readonly modalService = inject(ModalService);
  private readonly vcRef = inject(ViewContainerRef);

  //#endregion

  //#region Constants

  protected readonly ICONS = ICONS;

  //#endregion

  //#region Inputs/Outputs

  viewDropdown = viewChild('viewDropdownRef', { read: DropdownComponent });

  //#endregion

  //#region Properties

  popupContent = computed<Array<TViewEntityOption> | undefined>(() => {
    let content = this.viewSettingsGroups()
      ?.filter(
        (group: EditableViewSettingsGroup) => group && group?.views!.length > 0,
      )
      ?.flatMap((group: EditableViewSettingsGroup) =>
        group.views
          ?.filter((view) => view)
          ?.map(
            (view) =>
              ({
                label: view.name,
                value: view.name,
                group: group.name,
                view,
              }) as TViewEntityOption,
          )!,
      );
    return content;
  });

  //#endregion

  //Old props

  viewSettingsGroups = model<EditableViewSettingsGroup[]>();
  viewGroups = input<ViewGroup[]>();
  selectedView = model<ViewSettings>();
  withClearFilters = input<boolean>();
  isSaveDisabled = input<boolean>();

  viewGroupsOptions = computed(() =>
    this.viewGroups()
      ?.filter((group) => group.canEdit)
      ?.map((group) => ({ label: group.name, value: group.id })),
  );

  hasSelection = computed(() => !!this.selectedView());
  hasMultipleGroups = computed(() => (this.viewGroups()?.length ?? 0) > 1);
  hasViews = computed(() => {
    var x = this.viewSettingsGroups()?.some((g) => g && g?.views!.length > 0);
    return x;
  });

  onSelectView = output<any>();
  onSaveView = output();
  onSaveViewAs = output<any | undefined>();
  onEditViewName = output<string>();
  onDeleteView = output();
  onClearFilters = output();
  onSetGroupDefault = output();
  onSetFavoriteView = output<any>();

  radioCheck = signal(0);
  viewName = signal('');
  saveMode = signal('');
  showRadioGroup = signal(true);
  modalIsVisible = signal(false);

  isClearDisabled = signal(false);
  isSettingsOpen = signal(false);
  canClearFilters = signal(true);

  viewsMenu = viewChild('viewsMenu', { read: ElementRef });
  viewSelect = viewChild('viewSelect', { read: ElementRef });
  saveModal = viewChild('uiSaveViewModal', { read: ModalComponent });
  morePopup = viewChild('morePopupRef', { read: UiPopupDirective });

  toViewEntityOption(option: unknown) {
    return option as TViewEntityOption;
  }

  public showSaveView(mode: string) {
    this.radioCheck.set(this.viewGroups()?.find((x) => x.default)?.id ?? 1);
    this.saveMode.set(mode);
    this.viewName.set(
      this.saveMode() == 'edit' ? (this.selectedView()?.name ?? '') : '',
    );
    this.showRadioGroup.set(
      this.saveMode() === 'save' && !!this.viewGroups()?.some((x) => x.canEdit),
    );
    this.modalIsVisible.set(true);
    this.saveModal()?.open();
  }

  public showDeleteView() {
    this.modalService.openWarning({
      label: 'Delete View',
      text: 'Are you sure you want to delete this view?',
      actions: [
        {
          label: 'Delete',
          class: 'primary',
          action: () => this.onDeleteView.emit(),
        },
      ],
      vcRef: this.vcRef,
    });
  }

  onSave() {
    if (UTILS.STRING.isNullOrEmpty(this.viewName()))
      return this.toasterService.info('Please enter a name for the view');

    if (this.radioCheck() === 0)
      return this.toasterService.info('Please select a group for the view');

    if (this.saveMode() === 'edit') this.onEditViewName.emit(this.viewName());
    else
      this.onSaveViewAs.emit({
        name: this.viewName(),
        groupId: this.radioCheck(),
      });

    this.saveModal()?.close();
    this.viewName.set('');
  }

  saveView() {
    if (!this.selectedView() || !this.selectedView()!.canEdit)
      return this.showSaveView('save');

    this.onSaveView.emit();
  }

  selectView(option: unknown) {
    this.selectedView.set((option as { view: ViewSettings }).view);
    this.onSelectView.emit((option as { view: ViewSettings }).view);
  }

  setFavoriteView(view: ViewSettings) {
    this.onSetFavoriteView.emit(view);
    //this.isSettingsOpen.set(false);
  }

  closeModal() {
    //this.popoverService.close();
  }
}
